import React, { FunctionComponent, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  MeasurementSystem,
  TimelineEntryConnection,
  TimelineEntry as TimelineEntryModel,
  User,
} from '../../../../types/__generated__';
import useDeleteTimelineEntry from '../../../hooks/timeline/useDeleteTimelineEntry';
import LoadMoreButton from '../../LoadMoreButton/LoadMoreButton';
import TimelineEntry from '../TimelineEntry/TimelineEntry';

interface TimelineProps {
  fetchNextPage: () => void;
  fetchNextPost: () => void;
  timeline?: Partial<TimelineEntryConnection> | null;
  owner?: Partial<User> | null;
  measurementSystem?: MeasurementSystem;
}

const Timeline: FunctionComponent<TimelineProps> = ({
  fetchNextPage,
  fetchNextPost,
  timeline,
  owner,
  measurementSystem,
}) => {
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation('common', { useSuspense: false });
  const [deleteTimelineEntry] = useDeleteTimelineEntry(
    measurementSystem,
    handleFetchNextPost,
  );

  if (!timeline) {
    return null;
  }
  const { nodes = [], pageInfo } = timeline;
  const { hasNextPage } = pageInfo ?? {};

  async function handleFetchMore() {
    // Using state because Apollo does not render component with final data
    // with notifyOnNetworkStatus https://github.com/apollographql/apollo-client/issues/4574
    setLoading(true);
    await fetchNextPage();
    setLoading(false);
  }

  async function handleFetchNextPost() {
    if (nodes.length < 3 && hasNextPage) {
      setLoading(true);
      await fetchNextPost();
      setLoading(false);
    }
  }

  return (
    <div data-qa="timeline">
      {nodes.map((timelineEntry: TimelineEntryModel) => {
        return (
          <TimelineEntry
            key={timelineEntry.id}
            timelineEntry={timelineEntry}
            avatarUrl={owner?.avatar}
            postedBy={owner?.name}
            onDeleteEntry={deleteTimelineEntry}
          />
        );
      })}
      {hasNextPage && (
        <LoadMoreButton
          loading={loading}
          onClick={handleFetchMore}
          trackingData={{
            action_name: 'click',
            subtype: 'button',
            event: 'click',
            event_value: 'show more',
            sub_section: 'timeline',
          }}
        >
          {t('common.showMore', 'Show more')}
        </LoadMoreButton>
      )}
    </div>
  );
};

export default Timeline;
